/**
 * CR-7782: a call-off row's expanded view on the Call Off Management list —
 * the Items tab's lines and pallet / weight summary, without opening the
 * call-off.
 *
 * The list payload carries totals, not lines, so the call-off is fetched when
 * its row is opened, through the same scoped detail endpoint the modal uses
 * (a haulier gets it without prices, as there). It is fetched afresh on every
 * opening, so a row never shows lines from before an edit.
 *
 * @link CallOffLinesGrid for what is rendered.
 */
import { useCallback, useEffect, useState } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Loader2, AlertCircle } from 'lucide-react';
import CallOffLinesGrid from './CallOffLinesGrid';

interface CallOffExpandedItemsProps {
  callOffId: number;
}

export default function CallOffExpandedItems({ callOffId }: CallOffExpandedItemsProps) {
  const [callOff, setCallOff] = useState<CallOff | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    callOffAPI.getCallOff(callOffId)
      .then((res) => {
        if (!cancelled) setCallOff(res.data);
      })
      .catch((err: unknown) => {
        console.error('Could not load call-off items:', err);
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [callOffId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading items...
      </div>
    );
  }

  if (failed || !callOff) {
    return (
      <div className="flex items-center justify-center gap-3 py-6 text-sm text-red-700">
        <AlertCircle className="h-4 w-4" />
        Could not load the items.
        <Button variant="outline" size="sm" className="h-7" onClick={retry}>
          Retry
        </Button>
      </div>
    );
  }

  return <CallOffLinesGrid callOff={callOff} />;
}
